<template>
  <div>
    <b-modal
      :visible="visible"
      title="Persetujuan Umum (General Consent)"
      size="md"
      centered
      hide-footer
      @hidden="$emit('update:visible', false)"
    >
      <b-alert
        v-if="context === 'post-registration'"
        variant="warning"
        show
        class="mb-75 py-50 px-75"
      >
        <div class="d-flex align-items-start">
          <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 mt-25 text-warning" />
          <div>
            <div class="font-weight-bolder">Pendaftaran berhasil dibuat</div>
            <small>Persetujuan Umum belum ditandatangani — kirim tautan ke pasien sekarang.</small>
          </div>
        </div>
      </b-alert>

      <div v-if="loading" class="text-center py-1">
        <b-spinner small variant="primary" />
        <p class="text-muted small mt-50 mb-0">Memuat...</p>
      </div>

      <template v-else>
        <!-- Signed -->
        <div v-if="consent && consent.status === 'signed'">
          <b-alert variant="success" show class="mb-75 py-50 px-75">
            <div class="d-flex align-items-center">
              <feather-icon icon="CheckCircleIcon" size="16" class="mr-50 text-success" />
              <span class="font-weight-bold">
                Sudah ditandatangani
                <span v-if="consent.decision">— {{ consent.decision === 'agree' ? 'BERSEDIA' : 'MENOLAK' }}</span>
              </span>
            </div>
          </b-alert>
          <div class="d-flex align-items-center justify-content-between mb-50">
            <small class="text-muted">
              <feather-icon icon="ClockIcon" size="12" class="mr-25" />
              {{ formatExpiry(consent.signed_at) }}
            </small>
            <small v-if="consent.petugas_name" class="text-muted">Petugas: {{ consent.petugas_name }}</small>
          </div>
          <b-button
            v-if="consent.pdf_url"
            variant="primary"
            size="sm"
            block
            class="mb-50"
            @click="viewPdf"
          >
            <feather-icon icon="FileTextIcon" size="14" class="mr-25" />
            Lihat PDF Persetujuan
          </b-button>

          <b-button
            variant="outline-danger"
            size="sm"
            block
            :disabled="regenerating"
            @click="handleRegenerate"
          >
            <b-spinner v-if="regenerating" small class="mr-50" />
            <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-25" />
            Buat Ulang Persetujuan
          </b-button>
        </div>

        <!-- Active pending link -->
        <div v-else-if="consent && consent.is_active">
          <b-alert variant="info" show class="mb-75 py-50 px-75">
            <div class="d-flex align-items-center">
              <feather-icon icon="LinkIcon" size="16" class="mr-50 text-info" />
              <span class="font-weight-bold">Link aktif — menunggu tanda tangan pasien</span>
            </div>
          </b-alert>

          <div class="d-flex align-items-center mb-50">
            <feather-icon icon="UserIcon" size="12" class="mr-25 text-muted" />
            <small class="text-muted">
              Penandatangan:
              <span class="font-weight-bold">{{ consent.signer_type === 'representative' ? 'Diwakili keluarga' : 'Pasien sendiri' }}</span>
            </small>
          </div>

          <b-form-group label="Link untuk pasien" class="mb-50">
            <b-input-group>
              <b-form-input :value="consent.public_url" readonly class="bg-light" />
              <b-input-group-append>
                <b-button variant="outline-primary" @click="copyLink">
                  <feather-icon :icon="copied ? 'CheckIcon' : 'CopyIcon'" size="14" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-button variant="success" size="sm" block class="mb-75" @click="sendWhatsApp">
            <feather-icon icon="MessageCircleIcon" size="14" class="mr-25" />
            Kirim via WhatsApp
          </b-button>

          <div class="d-flex align-items-center justify-content-between mb-25">
            <small class="text-muted">
              <feather-icon icon="ClockIcon" size="12" class="mr-25" />
              Kadaluarsa: {{ formatExpiry(consent.expires_at) }}
            </small>
            <b-button variant="flat-danger" size="sm" @click="handleRevoke">
              <feather-icon icon="XCircleIcon" size="14" class="mr-25" />
              Nonaktifkan
            </b-button>
          </div>
        </div>

        <!-- No active link -->
        <div v-else class="text-center py-50">
          <feather-icon icon="FileTextIcon" size="32" class="text-muted mb-50" />
          <p class="text-muted small mb-0">
            Belum ada persetujuan umum aktif untuk pendaftaran ini.
            Pilih petugas lalu buat tautan untuk pasien menandatangani.
          </p>
        </div>

        <!-- Generate area — hidden once signed (re-sign via "Buat Ulang" above) -->
        <template v-if="!isSigned">
        <hr class="my-75">

        <b-form-group label="Penandatangan" class="mb-75">
          <b-form-radio-group v-model="selectedSignerType" class="gc-signer-types">
            <b-form-radio value="patient">Pasien sendiri</b-form-radio>
            <b-form-radio value="representative">Diwakili keluarga</b-form-radio>
          </b-form-radio-group>
          <small class="text-muted d-block mt-25">
            <feather-icon icon="InfoIcon" size="11" class="mr-25" />
            <template v-if="selectedSignerType === 'representative'">Data penanggung jawab wajib diisi pasien di formulir.</template>
            <template v-else>Bagian keluarga/penanggung jawab disembunyikan dari formulir.</template>
          </small>
        </b-form-group>

        <b-form-group label="Petugas penanggung jawab" class="mb-75">
          <v-select
            v-model="selectedPegawai"
            :options="pegawaiList"
            :loading="pegawaiLoading"
            :get-option-label="getPegawaiLabel"
            placeholder="Pilih petugas..."
            class="select-size-sm"
          />
        </b-form-group>

        <b-button
          variant="primary"
          size="sm"
          block
          :disabled="generating || !selectedPegawai"
          @click="handleGenerate"
        >
          <b-spinner v-if="generating" small class="mr-50" />
          <feather-icon v-else :icon="consent && consent.is_active ? 'RefreshCwIcon' : 'PlusIcon'" size="14" class="mr-25" />
          {{ consent && consent.is_active ? 'Buat Link Baru (link lama kadaluarsa)' : 'Buat Link Persetujuan' }}
        </b-button>
        </template>

        <b-button
          variant="outline-secondary"
          size="sm"
          block
          class="mt-50"
          @click="$emit('update:visible', false)"
        >
          <feather-icon icon="XIcon" size="14" class="mr-25" />
          Tutup
        </b-button>
      </template>
    </b-modal>

    <!-- Manual phone modal -->
    <b-modal v-model="showPhoneModal" title="Nomor WhatsApp Pasien" size="sm" hide-footer centered>
      <p class="small text-muted mb-50">Nomor WhatsApp pasien tidak tersedia. Masukkan nomor tujuan:</p>
      <b-form-input v-model="manualPhone" placeholder="08xxxxxxxxxx" size="sm" />
      <b-button variant="success" size="sm" block class="mt-75" :disabled="!manualPhone" @click="sendWithManualPhone">
        <feather-icon icon="MessageCircleIcon" size="14" class="mr-25" />
        Kirim
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import axios from '@axios'
import generalConsentService from '@/services/general-consent.service'
import WhatsAppService from '@/services/whatsapp-service'
import { useAuthStore } from '@/pinia/auth'
import moment from 'moment'

const whatsappService = new WhatsAppService()

export default defineComponent({
  components: { vSelect },
  props: {
    visible: { type: Boolean, default: false },
    pendaftaranId: { type: [Number, String], default: null },
    pasien: { type: Object, default: () => ({}) },
    // 'post-registration' explains an auto-opened modal; 'default' is the
    // deliberate open from the patient-detail button.
    context: { type: String, default: 'default' },
  },
  emits: ['update:visible', 'status-change'],
  setup(props, { root, emit }) {
    const loading = ref(false)
    const generating = ref(false)
    const regenerating = ref(false)
    const consent = ref(null)
    const isSigned = computed(() => !!(consent.value && consent.value.status === 'signed'))
    const copied = ref(false)
    const showPhoneModal = ref(false)
    const manualPhone = ref('')
    const pegawaiList = ref([])
    const pegawaiLoading = ref(false)
    const selectedPegawai = ref(null)
    const selectedSignerType = ref('patient')

    const toast = (variant, msg) => {
      if (root && root.$dekatoast && root.$dekatoast[variant]) root.$dekatoast[variant](msg)
    }

    // Mirror of the backend's GeneralConsent::deriveStatus so a list row and
    // this modal never disagree about the registration's consent state.
    const rowStatusOf = (value) => {
      if (!value) return 'none'
      if (value.status === 'signed') return 'signed'
      if (value.is_active) return 'pending'
      return 'none'
    }

    // Every consent assignment goes through here so the parent list can repaint
    // its row the moment we learn the real status, instead of waiting for a
    // full reload of the registration list.
    const setConsent = (value) => {
      consent.value = value
      emit('status-change', {
        pendaftaranId: props.pendaftaranId,
        status: rowStatusOf(value),
      })
    }

    const fetchStatus = async () => {
      if (!props.pendaftaranId) return
      loading.value = true
      try {
        const { data } = await generalConsentService.getStatus(props.pendaftaranId)
        setConsent(data.data || null)
      } catch (e) {
        setConsent(null)
      } finally {
        loading.value = false
      }
    }

    const loadPegawai = async () => {
      pegawaiLoading.value = true
      try {
        const { data } = await axios.get('/pegawai?limit=400')
        pegawaiList.value = (data.data || []).filter(p => p.status === 1)
      } catch (e) {
        pegawaiList.value = []
      } finally {
        pegawaiLoading.value = false
      }
    }

    const getPegawaiLabel = (o) => (o && (o.display_name || o.nama)) || ''

    const handleGenerate = async () => {
      if (!props.pendaftaranId || !selectedPegawai.value) return
      generating.value = true
      try {
        const { data } = await generalConsentService.generate(props.pendaftaranId, selectedPegawai.value.id, selectedSignerType.value)
        if (data.success) {
          setConsent(data.data)
          toast('success', 'Link persetujuan berhasil dibuat')
        }
      } catch (e) {
        toast('error', e)
      } finally {
        generating.value = false
      }
    }

    const handleRevoke = async () => {
      if (!consent.value) return
      try {
        await generalConsentService.revoke(consent.value.id)
        setConsent(null)
        toast('success', 'Link dinonaktifkan')
      } catch (e) {
        toast('error', e)
      }
    }

    const handleRegenerate = async () => {
      const result = await root.$swal({
        title: 'Buat Ulang Persetujuan?',
        text: 'PDF persetujuan sebelumnya akan dihapus dan pasien perlu menandatangani ulang.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, buat ulang',
        cancelButtonText: 'Batal',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return

      regenerating.value = true
      try {
        await generalConsentService.reset(props.pendaftaranId)
        // Drop the signed consent locally so the generate area reappears.
        setConsent(null)
        selectedSignerType.value = 'patient'
        selectedPegawai.value = null
        toast('success', 'Persetujuan sebelumnya dihapus. Silakan buat link baru.')
      } catch (e) {
        toast('error', e)
      } finally {
        regenerating.value = false
      }
    }

    const copyLink = () => {
      if (!consent.value) return
      navigator.clipboard.writeText(consent.value.public_url)
      copied.value = true
      setTimeout(() => { copied.value = false }, 2000)
    }

    const viewPdf = () => {
      if (consent.value && consent.value.pdf_url) window.open(consent.value.pdf_url, '_blank')
    }

    const buildMessage = () => {
      const authStore = useAuthStore()
      const entity = authStore.entity || {}
      const facilityName = entity.name || 'Klinik'
      const facilityPhone = entity.phone || '-'
      const patientName = (props.pasien && props.pasien.nama) || 'Bapak/Ibu'
      const url = consent.value && consent.value.public_url
      const expiryLine = consent.value && consent.value.expires_at
        ? `*Mohon ditandatangani sebelum ${moment(consent.value.expires_at).format('DD MMM YYYY HH:mm')}*.`
        : '*Mohon ditandatangani segera*.'

      return `Yth. *${patientName}*,\n\n`
        + `Mohon membaca dan menandatangani *Persetujuan Umum (General Consent)* dari *${facilityName}* melalui tautan berikut:\n\n`
        + `${url}\n\n`
        + `${expiryLine}\n\n`
        + `Jika ada pertanyaan, silakan hubungi kami di ${facilityPhone}.\n\n`
        + `Terima kasih.\n`
        + `🏥 *${facilityName}*`
    }

    const resolvePatientPhone = () => {
      const pasien = props.pasien || {}
      const kontak = Array.isArray(pasien.kontak_pasien) ? pasien.kontak_pasien : []
      const jenisOf = (k) => {
        if (k == null) return null
        if (typeof k.jenis === 'object' && k.jenis !== null) return k.jenis.value
        return k.jenis
      }
      const wa = kontak.find(k => (jenisOf(k) == 4) && k.nomor)
      if (wa) return wa.nomor
      const other = kontak.find(k => k.nomor)
      if (other) return other.nomor
      return pasien.no_hp || pasien.phone || pasien.telepon || pasien.handphone || pasien.hp || pasien.telp || null
    }

    const openWhatsAppFlow = (phone) => {
      try {
        whatsappService.openWhatsApp(phone, buildMessage())
      } catch (e) {
        toast('error', e.message || 'Gagal membuka WhatsApp')
      }
    }

    const sendWhatsApp = () => {
      if (!consent.value) return
      const phone = resolvePatientPhone()
      if (!phone) {
        manualPhone.value = ''
        showPhoneModal.value = true
        return
      }
      openWhatsAppFlow(phone)
    }

    const sendWithManualPhone = () => {
      if (!manualPhone.value) return
      if (!whatsappService.isValidPhoneNumber(manualPhone.value)) {
        toast('warning', 'Nomor WhatsApp tidak valid')
        return
      }
      openWhatsAppFlow(manualPhone.value)
      showPhoneModal.value = false
      manualPhone.value = ''
    }

    const formatExpiry = (dateStr) => (dateStr ? moment(dateStr).format('DD MMM YYYY HH:mm') : '-')

    watch(
      () => props.visible,
      (val) => {
        if (val && props.pendaftaranId) {
          selectedPegawai.value = null
          selectedSignerType.value = 'patient'
          fetchStatus()
          if (!pegawaiList.value.length) loadPegawai()
        }
      }
    )

    return {
      loading,
      generating,
      regenerating,
      consent,
      isSigned,
      copied,
      showPhoneModal,
      manualPhone,
      pegawaiList,
      pegawaiLoading,
      selectedPegawai,
      selectedSignerType,
      getPegawaiLabel,
      handleGenerate,
      handleRevoke,
      handleRegenerate,
      copyLink,
      viewPdf,
      sendWhatsApp,
      sendWithManualPhone,
      formatExpiry,
    }
  },
})
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
